/* eslint-disable @typescript-eslint/naming-convention */
import ComponentLoader from "@/../core/component-loader.vue";
import UserMenu from "@/shared/components/master-page/user-menu.vue";
import LegalWarning from "@/shared/components/master-page/legal-warning.vue";
import { Component, createApp } from "vue";
import { createPinia, type Pinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import PrimeVue from "primevue/config";
import RgLimitsBar from "@/shared/components/rg-limits/rg-limits-bar.vue";
import { usePopupsSequence } from "@/shared/components/master-page/use-popups-sequence";
import { getPrimevueConfig } from "@sazka/design-system/config/get-prime-vue-config";
import { AppRootEnum } from "./app-root-enum";

let pinia: Pinia;

const usePinia = () => {
    pinia = createPinia();
    pinia.use(piniaPluginPersistedstate);
};

const useVue = ({
    mountUserMenu = true,
    mountLegalWarning = true,
}: {
    mountUserMenu?: boolean;
    mountLegalWarning?: boolean;
}) => {
    usePinia();
    if (mountUserMenu) {
        useUserMenu();
    }
    if (mountLegalWarning) {
        useLegalWarning();
    }
    useRgLimitsBar();
    usePopupsSequence().start();
    useDynamicComponents();
    if (environment.isEditMode) {
        useEditModeObserver();
    }
};

function useUserMenu() {
    const component = createApp(UserMenu);
    component.use(pinia);
    component.mount("#user-menu");
}

function useLegalWarning() {
    if (environment.isInAppView) return;
    const component = createApp(LegalWarning);
    component.mount("#legal-warning");
}

function useRgLimitsBar() {
    const component = createApp(RgLimitsBar);
    component.use(pinia);
    component.mount("#rg-limits-bar");
}

function useDynamicComponents() {
    const roots = document.querySelectorAll("[data-vue]");

    for (const root of roots) {
        loadComponent(root as HTMLElement);
    }
}

function loadComponent(node: HTMLElement) {
    const componentName = node.getAttribute("data-vue");
    const appRoot = node.getAttribute("data-app-root") as AppRootEnum;
    const showLoader = !node.classList.contains("disable-loader");
    // pass data-attributes as props
    const dataset = node.dataset;
    const dataProps: Record<string, string | number | boolean> = {};
    Object.keys(dataset)
        .filter((x) => !["vapp", "vue", "loader"].includes(x.toLowerCase()))
        .forEach((p) => {
            let val: string | number | boolean = dataset[p];
            if (val === "false" || val === "False") {
                val = false;
            } else if (val === "true" || val === "True") {
                val = true;
            }
            dataProps[p] = val;
        });

    const component = createApp(ComponentLoader, { componentName, showLoader, dataProps, appRoot });
    component.use(pinia);
    component.use(PrimeVue, getPrimevueConfig());
    component.mount(node);
}

export function createCustomApp(component: Component) {
    const app = createApp(component);
    app.use(pinia);
    app.use(PrimeVue, getPrimevueConfig());
    return app;
}

function useEditModeObserver() {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (!mutation.addedNodes) return;

            for (const element of mutation.addedNodes) {
                const node = element as Element;
                if (!node.getAttribute) continue;

                const dataAttribute = node.getAttribute("data-vue");
                if (dataAttribute) {
                    loadComponent(node as HTMLElement);
                }
            }
        });
    });

    observer.observe(document.documentElement || document.body, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false,
    });
}

export { useVue };
